/**
 * A project's Flights tab (docs/specs/cloud/flights.md; a menu page until C8, decision 844): each flight of the
 * project with its daily entry's crew, times and hours, its km and lines and its Geosoft QC, sortable and filtered by
 * dates and aircraft, with totals; a flight opens on its lines, its daily entry, its QC and a small map of its lines.
 * Read only: flights, entries and reviews are made in Teslyn Field. Staff of the tenant only (clients never see
 * flights or daily entries; RLS refuses them too). Every figure is `@teslyn/domain`'s (flight-list.ts).
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  crewSlotWord,
  ENTRY_TYPE_LABEL,
  filterFlightRows,
  FLIGHT_QC_WORD,
  qcActionedSortValue,
  qcRunLinesText,
  flightAircraftChoices,
  flightFilterIssue,
  flightLines,
  flightMap,
  flightName,
  flightRows,
  flightTotals,
  formatKm,
  NO_FLIGHT_FILTER,
  REMAINING_LEGEND,
  type Acceptance,
  type FlightFilter,
  type FlightLine,
  type FlightQcState,
  type FlightRow,
  type LineFollowUp,
  type LineState,
  type QcVerdict,
  type SortState,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField, TextField } from '../components/Fields.tsx';
import { SortHeader, useSortableRows, type SortColumn } from '../components/SortableTable.tsx';
import { ErrorState, Loading, SectionHeader } from '../components/States.tsx';
import { dateText, longDay } from '../ops/format.ts';
import type { PortalProject } from '../portal/api.ts';
import { RemainingMap } from '../portal/RemainingMap.tsx';
import { loadFlightsData, type FlightsData } from './api.ts';

export interface ProjectFlightsProps {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
}

/** Hook: load once per key, with retry (as the project pages). */
function useLoad<T>(load: (() => Promise<T>) | null): {
  readonly data: T | null;
  readonly error: string | null;
  readonly reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(() => {
    let live = true;
    setError(null);
    setData(null);
    if (!load) return undefined;
    load()
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [load]);
  useEffect(run, [run]);
  return { data, error, reload: run };
}

const one = (v: number): string =>
  v.toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const km2 = (v: number): string => formatKm(v);
const dash = '–';

/**
 * A project's "Flights" tab (C8, decision 844; staff only, as the Flights page was): the filters, the list and one
 * flight's lines, entry, QC and map.
 */
export function ProjectFlights({ sb, project }: ProjectFlightsProps): ReactElement {
  const loadFlights = useMemo(
    () => () => loadFlightsData(sb, { id: project.id, tenantId: project.tenantId }),
    [sb, project.id, project.tenantId],
  );
  const flights = useLoad(loadFlights);
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<FlightFilter>(NO_FLIGHT_FILTER);
  const rows = useMemo(() => {
    const d = flights.data;
    if (!d) return [];
    // decision 751: the reviewers' names for the Actioned hover
    return flightRows({ ...d, reviewerName: (id) => d.reviewers.get(id) ?? null });
  }, [flights.data]);
  const openRow = rows.find((r) => r.flight.id === open) ?? null;

  if (openRow && flights.data) {
    return (
      <FlightDetail
        row={openRow}
        data={flights.data}
        project={project}
        onBack={() => setOpen(null)}
        onOpen={setOpen}
      />
    );
  }
  return (
    <div className="stack">
      <SectionHeader
        title="Flights"
        subtitle="Each flight of the project with its crew, hours, km, lines and QC, as Teslyn Field syncs them. Read only: flights, daily entries and line reviews are made in Teslyn Field."
      />
      <div className="flights__filters" role="group" aria-label="Filters">
        <TextField
          label="From"
          type="date"
          value={filter.from}
          onChange={(v) => setFilter({ ...filter, from: v })}
        />
        <TextField
          label="To"
          type="date"
          value={filter.to}
          onChange={(v) => setFilter({ ...filter, to: v })}
        />
        <SelectField
          label="Aircraft"
          value={filter.aircraft}
          onChange={(v) => setFilter({ ...filter, aircraft: v })}
          options={[
            ['', 'All aircraft'] as const,
            ...flightAircraftChoices(rows).map((a) => [a.key, a.label] as const),
          ]}
        />
        {(filter.from || filter.to || filter.aircraft) && (
          <Button variant="quiet" onClick={() => setFilter(NO_FLIGHT_FILTER)}>
            Show every flight
          </Button>
        )}
      </div>
      {flights.error && <ErrorState message={flights.error} onRetry={flights.reload} />}
      {!flights.data && !flights.error && <Loading what="flights" />}
      {flights.data && <FlightsTable rows={rows} filter={filter} onOpen={setOpen} />}
    </div>
  );
}

// ---------------------------------------------------------------- the list

const QC_CHIP: Readonly<Record<FlightQcState, string>> = {
  run_failed: 'chip--fail',
  fail: 'chip--fail',
  check: 'chip--warn',
  pass: 'chip--pass',
};

/** Decision 751: Pending in red, Complete in green, "—" with nothing to action; the hover says more. */
function ActionedChip({ row }: { readonly row: FlightRow }): ReactElement {
  const a = row.actioned;
  if (!a || a.state === 'nothing' || a.state === 'superseded')
    return (
      <span className="caption muted" title={a?.hover}>
        —
      </span>
    );
  return (
    <span className={`chip caption ${a.state === 'pending' ? 'chip--fail' : 'chip--pass'}`} title={a.hover}>
      {a.words}
    </span>
  );
}

function QcChip({ row }: { readonly row: FlightRow }): ReactElement {
  if (!row.qc) return <span className="caption muted">Not run</span>;
  return (
    <span
      className={`chip caption ${QC_CHIP[row.qc.state]}`}
      title={`${row.qc.run.label}: ${row.qc.run.fail_count} fail, ${row.qc.run.warn_count} to check, ${row.qc.run.pass_count} pass`}
    >
      {FLIGHT_QC_WORD[row.qc.state]}
    </span>
  );
}

/** Columns of a flight's row: the comments row under it spans them. */
const ROW_COLUMNS = 19;

/**
 * The daily entry's comments and QC comments under the flight's row, across the whole table, so the figures keep
 * their width beside the menu on a laptop (none when the entry has no comments).
 */
function CommentsRow({ row: r }: { readonly row: FlightRow }): ReactElement | null {
  if (r.entry && !r.entry.op_comment && !r.entry.qc_comment) return null;
  return (
    <tr className="flights__comments-row">
      <td colSpan={ROW_COLUMNS} className="table__text">
        <span className="sr-only">Comments on {flightName(r.flight).toLowerCase()}: </span>
        {r.entry ? (
          <>
            {r.entry.op_comment && <span className="table__line">{r.entry.op_comment}</span>}
            {r.entry.qc_comment && <span className="table__line muted">QC: {r.entry.qc_comment}</span>}
          </>
        ) : (
          <span className="muted">No daily entry</span>
        )}
      </td>
    </tr>
  );
}

const hoursOf = (
  r: FlightRow,
  k: 'survey_h' | 'comp_box_h' | 'cal_h' | 'ferry_h' | 'total_h',
): number | null => (r.hours ? r.hours[k] : null);

const COLUMNS: readonly SortColumn<FlightRow>[] = [
  {
    id: 'date',
    label: 'Date',
    firstDir: 'desc',
    value: (r) => [r.flight.date, r.takeoff, r.flight.flt_num],
  },
  { id: 'day', label: 'Day', className: 'num', firstDir: 'desc', value: (r) => r.flight.jday },
  { id: 'flight', label: 'Flight', className: 'num', value: (r) => r.flight.flt_num },
  { id: 'aircraft', label: 'Aircraft', value: (r) => r.rego },
  { id: 'crew', label: 'Crew', value: (r) => r.crew.map((c) => c.name).join(', ') },
  { id: 'takeoff', label: 'Take-off', className: 'num', value: (r) => r.takeoff },
  { id: 'landing', label: 'Landing', className: 'num', value: (r) => r.landing },
  { id: 'survey', label: 'Survey', className: 'num', firstDir: 'desc', value: (r) => hoursOf(r, 'survey_h') },
  {
    id: 'comp_box',
    label: 'Comp box',
    className: 'num',
    firstDir: 'desc',
    value: (r) => hoursOf(r, 'comp_box_h'),
  },
  { id: 'cals', label: 'Cals', className: 'num', firstDir: 'desc', value: (r) => hoursOf(r, 'cal_h') },
  { id: 'ferry', label: 'Ferry', className: 'num', firstDir: 'desc', value: (r) => hoursOf(r, 'ferry_h') },
  { id: 'total', label: 'Total', className: 'num', firstDir: 'desc', value: (r) => hoursOf(r, 'total_h') },
  {
    id: 'flown',
    label: 'Flown',
    className: 'num',
    firstDir: 'desc',
    value: (r) => r.flown_km,
    title:
      'Km flown: a line counts its planned length, or its logged length when overflown (as the daily entry).',
  },
  {
    id: 'accepted',
    label: 'Accepted',
    className: 'num',
    firstDir: 'desc',
    value: (r) => r.accepted_km,
    title: 'Km accepted in line review.',
  },
  {
    id: 'rejected',
    label: 'Rejected',
    className: 'num',
    firstDir: 'desc',
    value: (r) => r.rejected_km,
    title: 'Km to refly: rejected lines, and the part of a part-accepted line not accepted.',
  },
  {
    id: 'pending',
    label: 'Pending review',
    className: 'num',
    firstDir: 'desc',
    value: (r) => r.pending_km,
    title: 'Km flown on lines not reviewed yet.',
  },
  { id: 'lines', label: 'Lines', className: 'num', firstDir: 'desc', value: (r) => r.lines },
  {
    id: 'qc',
    label: 'QC',
    value: (r) => (r.qc ? ['run_failed', 'fail', 'check', 'pass'].indexOf(r.qc.state) : null),
  },
  {
    id: 'actioned',
    label: 'Actioned',
    value: (r) => (r.actioned ? qcActionedSortValue(r.actioned) : null),
    title:
      "Whether every Fail and Check line of the flight's newest QC run is acknowledged & accepted or reviewed with a reason or note.",
  },
];
const NEWEST_FIRST: SortState = { column: 'date', dir: 'desc' };
const byId = (id: string): SortColumn<FlightRow> => COLUMNS.find((c) => c.id === id)!;
const HOUR_COLUMNS = ['survey', 'comp_box', 'cals', 'ferry', 'total'] as const;
const KM_COLUMNS = ['flown', 'accepted', 'rejected', 'pending'] as const;

function FlightsTable({
  rows,
  filter,
  onOpen,
}: {
  readonly rows: readonly FlightRow[];
  readonly filter: FlightFilter;
  readonly onOpen: (flightId: string) => void;
}): ReactElement {
  const issue = flightFilterIssue(filter);
  const shown = useMemo(() => (issue ? [] : filterFlightRows(rows, filter)), [rows, filter, issue]);
  const sorted = useSortableRows('flights', shown, COLUMNS, NEWEST_FIRST);
  const t = flightTotals(shown);
  if (rows.length === 0) {
    return (
      <div className="panel">
        <p className="body">
          No flights on this project yet. A flight appears here when its log is imported in Teslyn Field and
          syncs.
        </p>
      </div>
    );
  }
  const head = (id: string): ReactElement => (
    <SortHeader key={id} column={byId(id)} sort={sorted.sort} onSort={sorted.onSort} />
  );
  return (
    <div className="stack">
      {issue && (
        <p className="notice notice--fail body" role="alert">
          {issue}
        </p>
      )}
      {!issue && shown.length === 0 && (
        <div className="panel">
          <p className="body">No flights match these dates and aircraft.</p>
        </div>
      )}
      {shown.length > 0 && (
        <div className="table-wrap">
          <table className="table table--flights" aria-label="Flights">
            <thead>
              <tr>
                {['date', 'day', 'flight', 'aircraft', 'crew', 'takeoff', 'landing'].map((id) => (
                  <SortHeader
                    key={id}
                    column={byId(id)}
                    sort={sorted.sort}
                    onSort={sorted.onSort}
                    rowSpan={2}
                  />
                ))}
                <th className="label table__group" colSpan={5} scope="colgroup">
                  Flight hours (h)
                </th>
                <th className="label table__group" colSpan={4} scope="colgroup">
                  Line km
                </th>
                {['lines', 'qc', 'actioned'].map((id) => (
                  <SortHeader
                    key={id}
                    column={byId(id)}
                    sort={sorted.sort}
                    onSort={sorted.onSort}
                    rowSpan={2}
                  />
                ))}
              </tr>
              <tr>
                {HOUR_COLUMNS.map(head)}
                {KM_COLUMNS.map(head)}
              </tr>
            </thead>
            <tbody>
              {sorted.rows.flatMap((r) => [
                <tr key={r.flight.id} className="flights__row">
                  <td className="data">{dateText(r.flight.date)}</td>
                  <td className="data num">{r.flight.jday}</td>
                  <td className="num">
                    <button
                      type="button"
                      className="link data"
                      aria-label={`Open ${flightName(r.flight).toLowerCase()}`}
                      onClick={() => onOpen(r.flight.id)}
                    >
                      {r.flight.flt_num}
                    </button>
                  </td>
                  <td className="data">{r.rego ?? dash}</td>
                  <td>
                    {r.crew.length > 0
                      ? r.crew.map((c) => (
                          <span key={c.slot} className="table__line" title={crewSlotWord(c.slot)}>
                            {c.name}
                          </span>
                        ))
                      : dash}
                  </td>
                  <td className="data num">{r.takeoff ?? dash}</td>
                  <td className="data num">{r.landing ?? dash}</td>
                  {(['survey_h', 'comp_box_h', 'cal_h', 'ferry_h', 'total_h'] as const).map((k) => (
                    <td key={k} className="data num">
                      {r.hours ? one(r.hours[k]) : dash}
                    </td>
                  ))}
                  <td className="data num">{km2(r.flown_km)}</td>
                  <td className="data num">{km2(r.accepted_km)}</td>
                  <td className="data num">{km2(r.rejected_km)}</td>
                  <td className="data num">{km2(r.pending_km)}</td>
                  <td className="data num">{r.lines}</td>
                  <td>
                    <QcChip row={r} />
                  </td>
                  <td>
                    <ActionedChip row={r} />
                  </td>
                </tr>,
                <CommentsRow key={`${r.flight.id}-comments`} row={r} />,
              ])}
            </tbody>
            <tfoot>
              <tr className="progress-table__total">
                <th scope="row" className="body" colSpan={7}>
                  {t.flights === 1 ? '1 flight' : `${t.flights} flights`}
                  {t.withEntry < t.flights ? ` (${t.flights - t.withEntry} with no daily entry)` : ''}
                </th>
                <td className="data num">{one(t.survey_h)}</td>
                <td className="data num">{one(t.comp_box_h)}</td>
                <td className="data num">{one(t.cal_h)}</td>
                <td className="data num">{one(t.ferry_h)}</td>
                <td className="data num">{one(t.total_h)}</td>
                <td className="data num">{km2(t.flown_km)}</td>
                <td className="data num">{km2(t.accepted_km)}</td>
                <td className="data num">{km2(t.rejected_km)}</td>
                <td className="data num">{km2(t.pending_km)}</td>
                <td className="data num">{t.lines}</td>
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <p className="caption muted">
        Hours are the daily entry&apos;s, as Teslyn Field fills them from the flight log and the crew checks
        them: survey from the start of the first survey line to the end of the last, comp box its own lines,
        ferry the rest; a flight with no daily entry has none. Km flown counts each line at its planned length
        (its logged length when overflown), accepted counts reviewed lines only, rejected is the km to refly,
        and pending review the km of lines not reviewed yet.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- one flight

const ACCEPTANCE: Readonly<Record<Acceptance, { readonly chip: string; readonly word: string }>> = {
  accepted: { chip: 'chip--pass', word: 'Accepted' },
  partial: { chip: 'chip--warn', word: 'Partial' },
  rejected: { chip: 'chip--fail', word: 'Rejected' },
  pending: { chip: '', word: 'Pending review' },
};

const VERDICT: Readonly<Record<QcVerdict, { readonly chip: string; readonly word: string }>> = {
  pass: { chip: 'chip--pass', word: 'Pass' },
  warn: { chip: 'chip--warn', word: 'Check' },
  fail: { chip: 'chip--fail', word: 'Fail' },
  not_run: { chip: '', word: 'Not run' },
};

const ROLE_WORD = { reflight: 'reflight', patch: 'patch' } as const;

function reviewedText(at: string | null): string {
  if (!at) return '';
  return new Date(at).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function FlightDetail({
  row,
  data,
  project,
  onBack,
  onOpen,
}: {
  readonly row: FlightRow;
  readonly data: FlightsData;
  readonly project: PortalProject;
  readonly onBack: () => void;
  readonly onOpen: (flightId: string) => void;
}): ReactElement {
  const f = row.flight;
  const lines = useMemo(
    () =>
      flightLines({
        flight: f,
        statuses: data.statuses,
        plan: data.plan,
        blocks: data.blocks,
        flights: data.flights,
        qcLines: data.qcLines,
      }),
    [f, data],
  );
  const map = useMemo(
    () => flightMap(f.id, data.statuses, data.plan, data.blocks, { epsg: project.epsg }),
    [f.id, data, project.epsg],
  );
  const e = row.entry;
  const blockNames = e
    ? e.blocks.map((id) => data.blocks.find((b) => b.id === id)?.name).filter((n): n is string => Boolean(n))
    : [];
  const linkTo = (target: LineFollowUp['flight']): ReactElement | string => {
    if (!target) return '';
    if (target.id === f.id) return 'this flight';
    return (
      <button type="button" className="link" onClick={() => onOpen(target.id)}>
        {flightName(target).toLowerCase()}
      </button>
    );
  };
  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h2 className="heading page__title">{flightName(f)}</h2>
          <p className="body muted page__sub">
            {[longDay(f.date), row.rego ?? 'No aircraft recorded', `${project.number} ${project.title}`].join(
              ' · ',
            )}
          </p>
        </div>
        <div className="row">
          <Button variant="quiet" onClick={onBack}>
            All flights
          </Button>
        </div>
      </header>

      <dl className="flight-facts panel" aria-label="The flight">
        <dt className="label muted">Crew</dt>
        <dd className="body">
          {row.crew.length > 0
            ? row.crew.map((c) => `${c.name} (${crewSlotWord(c.slot).toLowerCase()})`).join(', ')
            : dash}
        </dd>
        <dt className="label muted">Take-off and landing</dt>
        <dd className="data">
          {row.takeoff ?? dash} to {row.landing ?? dash}
        </dd>
        <dt className="label muted">Flight hours</dt>
        <dd className="data">
          {row.hours
            ? `${one(row.hours.total_h)} h: survey ${one(row.hours.survey_h)} h, comp box ${one(row.hours.comp_box_h)} h, cals ${one(row.hours.cal_h)} h, ferry ${one(row.hours.ferry_h)} h`
            : 'No daily entry: no hours'}
        </dd>
        <dt className="label muted">Line km</dt>
        <dd className="data">
          {km2(row.flown_km)} km flown, {km2(row.accepted_km)} km accepted, {km2(row.rejected_km)} km
          rejected, {km2(row.pending_km)} km pending review
        </dd>
        <dt className="label muted">Lines</dt>
        <dd className="data">
          {row.lines}
          {row.pendingLines > 0 ? `, ${row.pendingLines} pending review` : ''}
        </dd>
      </dl>

      <h2 className="heading">Lines</h2>
      {lines.length === 0 ? (
        <div className="panel">
          <p className="body">No survey lines on this flight (a ferry, comp box or calibration flight).</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table table--flights" aria-label={`Lines of ${flightName(f).toLowerCase()}`}>
            <thead>
              <tr>
                <th className="label num" scope="col">
                  Line
                </th>
                <th className="label" scope="col">
                  Block
                </th>
                <th className="label num" scope="col">
                  Direction
                </th>
                <th className="label num" scope="col">
                  Flown (km)
                </th>
                <th className="label num" scope="col">
                  Accepted (km)
                </th>
                <th className="label" scope="col">
                  Review
                </th>
                <th className="label" scope="col">
                  Reason and note
                </th>
                <th className="label" scope="col">
                  Reviewed
                </th>
                <th className="label" scope="col">
                  Refly
                </th>
                <th className="label" scope="col">
                  QC
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <LineRow key={l.status.id} line={l} reviewers={data.reviewers} linkTo={linkTo} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="heading">Daily entry</h2>
      {e ? (
        <dl className="flight-facts panel" aria-label="Daily entry">
          <dt className="label muted">Entry</dt>
          <dd className="body">
            {ENTRY_TYPE_LABEL[e.entry_type]}, {dateText(e.date)}, flight {e.flt_num}
          </dd>
          {blockNames.length > 0 && (
            <>
              <dt className="label muted">Blocks</dt>
              <dd className="body">{blockNames.join(', ')}</dd>
            </>
          )}
          <dt className="label muted">Fuel</dt>
          <dd className="data">
            {Math.round(e.fuel_used_l).toLocaleString('en-AU')} L used
            {e.fuel_load_l > 0 ? `, ${Math.round(e.fuel_load_l).toLocaleString('en-AU')} L loaded` : ''}
          </dd>
          {e.starts > 0 && (
            <>
              <dt className="label muted">Engine starts</dt>
              <dd className="data">{e.starts}</dd>
            </>
          )}
          <dt className="label muted">Comments</dt>
          <dd className="body prose-wrap">{e.op_comment || dash}</dd>
          <dt className="label muted">QC comments</dt>
          <dd className="body prose-wrap">{e.qc_comment || dash}</dd>
        </dl>
      ) : (
        <div className="panel">
          <p className="body">
            No daily entry has been saved for this flight in Teslyn Field, so it has no crew or hours here.
          </p>
        </div>
      )}

      <h2 className="heading">Flight data QC</h2>
      {row.qc ? (
        <div className="panel stack" aria-label="Flight data QC">
          <p className="body">
            <span className={`chip caption ${QC_CHIP[row.qc.state]}`}>{FLIGHT_QC_WORD[row.qc.state]}</span>{' '}
            {row.qc.run.label} ({row.qc.run.family.toUpperCase()}), run {reviewedText(row.qc.run.started_at)}
            {row.qc.runs > 1 ? `, the newest of ${row.qc.runs} runs` : ''}
          </p>
          {row.qc.run.status === 'failed' ? (
            <p className="body">The run failed: {row.qc.run.error || 'no reason recorded'}.</p>
          ) : (
            <p className="data">
              {qcRunLinesText(row.qc.run)}: {row.qc.run.fail_count} fail, {row.qc.run.warn_count} to check,{' '}
              {row.qc.run.pass_count} pass
            </p>
          )}
          <p className="caption muted">Each line&apos;s tests are in Flight data QC in Teslyn Field.</p>
        </div>
      ) : (
        <div className="panel">
          <p className="body">Flight data QC has not been run on this flight in Teslyn Field.</p>
        </div>
      )}

      {map.features.length > 0 && (
        <>
          <h2 className="heading">Map</h2>
          <RemainingMap
            features={map.features}
            label={`Map of the lines of ${flightName(f).toLowerCase()}: ${lines.length} lines.`}
          />
          <ul className="legend" aria-label="Legend">
            {(Object.keys(map.counts) as LineState[]).map((s) => (
              <li key={s} className="legend__item caption">
                <span
                  className={`legend__swatch legend__swatch--${REMAINING_LEGEND[s].stroke}${REMAINING_LEGEND[s].dashed ? ' legend__swatch--dashed' : ''}`}
                  style={{ borderColor: `var(--${REMAINING_LEGEND[s].colour})` }}
                  aria-hidden="true"
                />
                {REMAINING_LEGEND[s].word} ({map.counts[s]})
              </li>
            ))}
          </ul>
          <p className="caption muted">
            The lines this flight flew, each in its state now, as on the lines-remaining map: a line rejected
            here and reflown since shows as its reflight stands.
          </p>
          {map.notDrawn.length > 0 && (
            <div className="notice notice--warn" role="status">
              <p className="label">Not drawn</p>
              <ul>
                {map.notDrawn.map((n) => (
                  <li key={n} className="body">
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LineRow({
  line: l,
  reviewers,
  linkTo,
}: {
  readonly line: FlightLine;
  readonly reviewers: ReadonlyMap<string, string>;
  readonly linkTo: (target: LineFollowUp['flight']) => ReactElement | string;
}): ReactElement {
  const s = l.status;
  const a = ACCEPTANCE[s.acceptance];
  const reviewer = s.reviewed_by ? (reviewers.get(s.reviewed_by) ?? 'Someone') : null;
  return (
    <tr>
      <td className="data num">
        {s.line_number}
        {l.role !== 'original' && <span className="table__line caption muted">{ROLE_WORD[l.role]}</span>}
      </td>
      <td>{l.block ?? dash}</td>
      <td className="data num">{s.direction_deg === null ? dash : `${s.direction_deg}°`}</td>
      <td className="data num">{km2(l.flown_km)}</td>
      <td className="data num">{km2(l.accepted_km)}</td>
      <td>
        <span className={`chip caption ${a.chip}`}>{a.word}</span>
        {!s.use_km && s.acceptance !== 'pending' && (
          <span className="table__line caption muted">km not counted</span>
        )}
      </td>
      <td className="table__text">
        {s.reason && <span className="table__line">{s.reason}</span>}
        {s.note && <span className="table__line muted">{s.note}</span>}
        {!s.reason && !s.note && dash}
      </td>
      <td className="table__text">
        {reviewer || s.reviewed_at ? (
          <>
            {reviewer && <span className="table__line">{reviewer}</span>}
            {s.reviewed_at && (
              <span className="table__line caption muted">{reviewedText(s.reviewed_at)}</span>
            )}
          </>
        ) : (
          dash
        )}
      </td>
      <td className="table__text">
        {l.reflies && (
          <span className="table__line">
            Reflies {l.reflies.line_number}
            {l.reflies.flight ? <> of {linkTo(l.reflies.flight)}</> : ''}
          </span>
        )}
        {l.followUps.map((u) => (
          <span key={u.line_number} className="table__line">
            {u.flight ? (
              <>
                Reflown as {u.line_number} ({ROLE_WORD[u.role]}) on {linkTo(u.flight)}
                {u.acceptance ? `, ${ACCEPTANCE[u.acceptance].word.toLowerCase()}` : ''}
              </>
            ) : (
              <>
                {u.line_number} {ROLE_WORD[u.role]} planned
              </>
            )}
          </span>
        ))}
        {!l.reflies && l.followUps.length === 0 && dash}
      </td>
      <td>
        {l.qc ? (
          <span className={`chip caption ${VERDICT[l.qc].chip}`}>{VERDICT[l.qc].word}</span>
        ) : (
          <span className="caption muted">{dash}</span>
        )}
      </td>
    </tr>
  );
}
